import { useReducer, useRef, useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { post, getErrorMessage } from '../utils/api';
import type {
  TriviaRushState,
  TriviaRushAction,
  TriviaStartResponse,
  TriviaAnswerResult,
  TriviaQuestion,
} from '../types/triviaRush';

const TICK_INTERVAL_MS = 100;
const ANSWER_FEEDBACK_DELAY_MS = 1200;

const initialState: TriviaRushState = {
  status: 'idle',
  sessionId: null,
  questions: [],
  currentIndex: 0,
  score: 0,
  lastResult: null,
  timeRemaining: 10000,
  timerDuration: 10000,
  error: null,
};

function triviaRushReducer(state: TriviaRushState, action: TriviaRushAction): TriviaRushState {
  switch (action.type) {
    case 'START_LOADING':
      return { ...initialState, status: 'loading' };

    case 'START_GAME':
      return {
        ...state,
        status: 'playing',
        sessionId: action.sessionId,
        questions: action.questions,
        currentIndex: 0,
        score: 0,
        lastResult: null,
        timeRemaining: action.timerDuration,
        timerDuration: action.timerDuration,
        error: null,
      };

    case 'TICK':
      if (state.status !== 'playing') return state;
      const newTime = Math.max(0, state.timeRemaining - action.delta);
      if (newTime <= 0) {
        return { ...state, status: 'game-over', timeRemaining: 0 };
      }
      return { ...state, timeRemaining: newTime };

    case 'SUBMIT_ANSWER':
      if (state.status !== 'playing') return state;
      return { ...state, status: 'answered' };

    case 'ANSWER_RESULT':
      return {
        ...state,
        lastResult: action.result,
        score: action.result.score,
      };

    case 'NEXT_QUESTION': {
      const nextQ = state.lastResult?.nextQuestion;
      // Drop answered questions from front, keep only remaining + new
      const remaining = state.questions.slice(state.currentIndex + 1);
      const updatedQuestions = nextQ ? [...remaining, nextQ] : remaining;
      return {
        ...state,
        status: 'playing',
        currentIndex: 0,
        questions: updatedQuestions,
        lastResult: null,
        timeRemaining: state.timerDuration,
      };
    }

    case 'TIMEOUT':
      return { ...state, status: 'game-over', timeRemaining: 0 };

    case 'GAME_OVER':
      return { ...state, status: 'game-over' };

    case 'RESET':
      return { ...initialState };

    case 'SET_ERROR': {
      // Mid-game errors go to game-over; pre-game errors go to idle
      const wasInGame = state.status === 'playing' || state.status === 'answered';
      return { ...state, status: wasInGame ? 'game-over' : 'idle', error: action.error };
    }

    default:
      return state;
  }
}

export interface UseTriviaRushOptions {
  demo?: boolean;
}

export function useTriviaRush(isDesktop: boolean, options?: UseTriviaRushOptions) {
  const [state, dispatch] = useReducer(triviaRushReducer, initialState);
  const isDemo = options?.demo ?? false;
  const queryClient = useQueryClient();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastTickRef = useRef<number>(0);
  const feedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Timer management
  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    stopTimer();
    lastTickRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const now = Date.now();
      const delta = now - lastTickRef.current;
      lastTickRef.current = now;
      dispatch({ type: 'TICK', delta });
    }, TICK_INTERVAL_MS);
  }, [stopTimer]);

  // Clear timers on unmount
  useEffect(() => {
    return () => {
      stopTimer();
      if (feedbackTimeoutRef.current) {
        clearTimeout(feedbackTimeoutRef.current);
      }
    };
  }, [stopTimer]);

  // Start/stop timer based on status
  useEffect(() => {
    if (state.status === 'playing') {
      startTimer();
    } else {
      stopTimer();
    }
  }, [state.status, startTimer, stopTimer]);

  // When the game ends via timeout (no lastResult), notify backend to save score
  useEffect(() => {
    if (state.status === 'game-over' && state.sessionId && !state.lastResult) {
      post('/api/trivia-rush/end-session', { sessionId: state.sessionId })
        .finally(() => {
          queryClient.invalidateQueries({ queryKey: ['rush-leaderboard'] });
        });
    }
  }, [state.status, state.sessionId, state.lastResult, queryClient]);

  const startGame = useCallback(async () => {
    dispatch({ type: 'START_LOADING' });
    // Fire-and-forget analytics
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
    fetch(`${apiBaseUrl}/api/analytics/start-rush-click`, { method: 'POST' }).catch(() => {});
    try {
      const response = await post<TriviaStartResponse>(
        '/api/trivia-rush/start',
        isDemo ? { demo: true } : undefined
      );
      const data = response.data;
      dispatch({
        type: 'START_GAME',
        sessionId: data.sessionId,
        questions: data.questions,
        timerDuration: data.timerDuration,
      });
    } catch (err) {
      dispatch({ type: 'SET_ERROR', error: getErrorMessage(err) });
    }
  }, [isDemo]);

  const submitAnswer = useCallback(
    async (choice: 'A' | 'B') => {
      if (state.status !== 'playing' || !state.sessionId) return;

      const currentQuestion = state.questions[state.currentIndex];
      if (!currentQuestion) return;

      dispatch({ type: 'SUBMIT_ANSWER' });

      try {
        const response = await post<TriviaAnswerResult>('/api/trivia-rush/answer', {
          sessionId: state.sessionId,
          questionId: currentQuestion.questionId,
          choice,
        });
        const result = response.data;

        dispatch({ type: 'ANSWER_RESULT', result });

        if (result.isGameOver) {
          queryClient.invalidateQueries({ queryKey: ['rush-leaderboard'] });
          feedbackTimeoutRef.current = setTimeout(() => {
            dispatch({ type: 'GAME_OVER' });
          }, ANSWER_FEEDBACK_DELAY_MS);
        } else {
          feedbackTimeoutRef.current = setTimeout(() => {
            dispatch({ type: 'NEXT_QUESTION' });
          }, ANSWER_FEEDBACK_DELAY_MS);
        }
      } catch (err) {
        dispatch({ type: 'SET_ERROR', error: getErrorMessage(err) });
      }
    },
    [state.status, state.sessionId, state.questions, state.currentIndex, queryClient]
  );

  // Leaderboard: open by default on desktop, close during game, reopen on game over
  const [showLeaderboard, setShowLeaderboard] = useState(isDesktop);

  useEffect(() => {
    if (state.status === 'game-over') {
      if (isDesktop) setShowLeaderboard(true);
    } else if (state.status === 'loading' || state.status === 'playing') {
      setShowLeaderboard(false);
    }
  }, [state.status, isDesktop]);

  const toggleLeaderboard = useCallback(() => {
    setShowLeaderboard(prev => !prev);
  }, []);

  const playAgain = useCallback(() => {
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
    }
    setShowLeaderboard(false);
    dispatch({ type: 'RESET' });
  }, []);

  const currentQuestion: TriviaQuestion | null =
    state.questions[state.currentIndex] ?? null;

  return {
    state,
    currentQuestion,
    startGame,
    submitAnswer,
    playAgain,
    showLeaderboard,
    toggleLeaderboard,
  };
}
